// dashboard/ComingSoonModal.tsx
import React from 'react';
import { X, Wallet, Plus, Shield, Sparkles } from 'lucide-react';

interface ComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  icon: 'wallet' | 'plus' | 'shield';
}

export const ComingSoonModal: React.FC<ComingSoonModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  icon,
}) => {
  if (!isOpen) return null;

  const renderIcon = () => {
    const iconClass = "w-64 h-64";
    switch (icon) {
      case 'wallet':
        return <Wallet className={iconClass} />;
      case 'plus':
        return <Plus className={iconClass} />;
      case 'shield':
        return <Shield className={iconClass} />;
      default:
        return <Sparkles className={iconClass} />;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-16 md:p-24">
        <div
          className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 animate-in fade-in zoom-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-16 right-16 p-8 rounded-full hover:bg-gray-100 transition-colors duration-200 group"
            aria-label="Close modal"
          >
            <X className="w-20 h-20 text-gray-400 group-hover:text-gray-600" />
          </button>

          {/* Content */}
          <div className="p-32 md:p-40">
            {/* Icon with gradient background */}
            <div className="flex justify-center mb-24">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-purple via-brand-indigo to-brand-pink rounded-full blur-xl opacity-50 animate-pulse" />
                <div className="relative bg-gradient-to-br from-brand-purple via-brand-indigo to-brand-pink p-20 rounded-full text-white">
                  {renderIcon()}
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-24 md:text-28 font-bold text-gray-900 text-center mb-16">
              {title}
            </h2>

            {/* Coming Soon Badge */}
            <div className="flex justify-center mb-20">
              <span className="inline-flex items-center gap-8 px-16 py-8 rounded-full bg-gradient-to-r from-brand-purple/10 to-brand-pink/10 border border-brand-purple/20">
                <Sparkles className="w-16 h-16 text-brand-purple" />
                <span className="text-14 font-semibold bg-gradient-to-r from-brand-purple to-brand-pink bg-clip-text text-transparent">
                  Coming Soon
                </span>
              </span>
            </div>

            {/* Message */}
            <p className="text-16 text-gray-600 text-center leading-relaxed mb-32">
              {message}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-12">
              <button
                onClick={onClose}
                className="flex-1 px-24 py-14 bg-gradient-to-r from-brand-purple via-brand-indigo to-brand-pink text-white font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200"
              >
                Got it!
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-24 py-14 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all duration-200"
              >
                Close
              </button>
            </div>

            {/* Footer Note */}
            <p className="text-12 text-gray-400 text-center mt-20">
              We're working hard to bring you this feature!
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

